import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@reducers/index';

import { useProperties } from '@modules/Properties/useProperties';
import type { PropertyRecord } from '@modules/Properties/types';

/* The organization IS the property: every page of the workspace reads the one
 * property this organization tracks. Older organizations may hold several: an
 * active one beats a draft (a half-set-up draft is not "the" property), then the
 * most recently updated wins — until the user picks another, and that pick is
 * remembered per organization. */

const storageKey = (orgId: string) => `workspaceProperty:${orgId}`;

const readChoice = (orgId?: string): string | null => {
  if (!orgId) return null;
  try {
    return localStorage.getItem(storageKey(orgId));
  } catch {
    return null;
  }
};

const STATUS_RANK: Record<string, number> = {
  ACTIVE: 0,
  PAUSED: 1,
  COMPLETED: 2,
  DRAFT: 3,
  ARCHIVED: 4,
};

export const pickWorkspaceProperty = (
  properties: PropertyRecord[],
  chosenId: string | null,
): PropertyRecord | null => {
  const chosen = chosenId && properties.find((p) => p.id === chosenId);
  if (chosen) return chosen;
  const rank = (p: PropertyRecord) =>
    STATUS_RANK[p.status.toUpperCase()] ?? STATUS_RANK.DRAFT;
  return (
    [...properties].sort(
      (a, b) =>
        rank(a) - rank(b) ||
        Date.parse(b.updatedAt || '') - Date.parse(a.updatedAt || ''),
    )[0] || null
  );
};

export const useWorkspaceProperty = () => {
  const orgId = useSelector(
    (state: RootState) => state.SelectedOrganization?._id as string | undefined,
  );
  const orgToken = useSelector(
    (state: RootState) =>
      (state.SelectedOrganization as { token?: string } | null)?.token,
  );
  const { data, loading, error, refetch } = useProperties();
  /* The list is read with the session's org token, which the boot re-sync can
   * swap after this hook mounted (a persisted selection without its token, or
   * a switch). Without a refetch the page shows the previous org's property. */
  const scope = `${orgId}:${orgToken || ''}`;
  const lastScope = useRef(scope);
  useEffect(() => {
    if (lastScope.current === scope) return;
    lastScope.current = scope;
    refetch();
    // `refetch` is a fresh closure every render; the scope is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);
  /* Until that refetch lands, the list in hand is the previous scope's: hide
   * it (loading) and read the pick of the new organization, not the old one. */
  const [scoped, setScoped] = useState(() => ({
    scope,
    stale: null as PropertyRecord[] | null,
    choice: readChoice(orgId),
  }));
  if (scoped.scope !== scope) {
    setScoped({ scope, stale: data, choice: readChoice(orgId) });
  }
  const pending = scoped.scope !== scope || (!!data && data === scoped.stale);
  const properties = useMemo(
    () => (pending ? [] : data || []),
    [data, pending],
  );
  const property = useMemo(
    () => pickWorkspaceProperty(properties, scoped.choice),
    [properties, scoped.choice],
  );

  // Remember only the id for parallel reads on the next visit. It never
  // overrides selection: the current server list remains authoritative.
  useEffect(() => {
    if (!orgId || loading || pending || error || !data) return;
    try {
      const key = `workspaceLastProperty:${orgId}`;
      if (property) localStorage.setItem(key, property.id);
      else localStorage.removeItem(key);
    } catch {
      /* Private mode. */
    }
  }, [orgId, loading, pending, error, data, property]);

  const choose = useCallback(
    (propertyId: string) => {
      setScoped((prev) => ({ ...prev, choice: propertyId }));
      if (!orgId) return;
      try {
        localStorage.setItem(storageKey(orgId), propertyId);
      } catch {
        // Private mode: the pick lasts for this session only.
      }
    },
    [orgId],
  );

  return {
    property,
    properties,
    loading: (loading && !data) || pending,
    error,
    refetch,
    choose,
  };
};
